import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  SearchIcon,
  SettingsIcon,
  ShapesIcon,
} from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { UploadLogFilterType } from '~/client/graph'
import DateSelector from '~/client/src/desktop/components/Filters/DateSelector/DateSelector'
import ColumnsConfigurationMenu from '~/client/src/desktop/components/HeaderBar/components/ColumnsConfigurationMenu'
import ViewModeToggle from '~/client/src/desktop/components/HeaderBar/components/ViewModeToggle/ViewModeToggle'
import HeaderBarStore from '~/client/src/desktop/components/HeaderBar/HeaderBar.store'
import {
  BUILT_IN_FILTER_TYPES,
  FilterTypeDescriptor,
  SavedViews,
} from '~/client/src/desktop/components/SavedViews'
import { TwoMonthsDatePickerMode } from '~/client/src/desktop/components/TwoMonthsDatePicker/TwoMonthsDatePicker.store'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import EntityNameFilter from '~/client/src/shared/components/Filters/EntityNameFilter/EntityNameFilter'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  AttachmentGroupingOption,
  attachmentsGroupingOptionList,
} from '~/client/src/shared/enums/AttachmentsGroupingOption'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import KnownTranslatorKeys from '~/client/src/shared/localization/knownTranslatorKeys'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import UserProject from '~/client/src/shared/models/UserProject'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { NOOP } from '~/client/src/shared/utils/noop'

import AttachmentsStore from '../../Attachments.store'
import { useAttachmentsSavedViews } from '../../hooks/useAttachmentsSavedViews'
import AttachmentFilterStore from './components/AttachmentsFilters/AttachmentFilter.store'
import AttachmentsFilters from './components/AttachmentsFilters/AttachmentsFilters'
import AttachmentsGroupingOptions from './components/AttachmentsGroupingOptions/AttachmentsGroupingOptions'
import AttachmentsGroupingOptionsStore from './components/AttachmentsGroupingOptions/AttachmentsGroupingOptions.store'
import AttachmentsSortingOptions from './components/AttachmentsSortingOptions/AttachmentsSortingOptions'
import AttachmentsSortingOptionsStore from './components/AttachmentsSortingOptions/AttachmentsSortingOptions.store'

const datePickerStates = [
  TwoMonthsDatePickerMode.ONE_DAY,
  TwoMonthsDatePickerMode.FIXED_WEEK,
  TwoMonthsDatePickerMode.MONTH,
  TwoMonthsDatePickerMode.PROJECT,
]

const ICON_SIZE = 16

const FACET_LABELS: Record<string, string> = {
  [UploadLogFilterType.Company]: 'Company',
  [UploadLogFilterType.UploadedBy]: 'Uploaded By',
  [UploadLogFilterType.Location]: 'Location',
  [UploadLogFilterType.Filetype]: 'File Type',
  [UploadLogFilterType.FileExtension]: 'File Extension',
  [UploadLogFilterType.Source]: 'Source',
  [UploadLogFilterType.Formtype]: 'Form Type',
  [UploadLogFilterType.WorkflowCategory]: 'Workflow Category',
  [UploadLogFilterType.DateUploaded]: 'Date Uploaded',
}

const ATTACHMENTS_FACETS: FilterTypeDescriptor[] = [
  {
    key: UploadLogFilterType.Company,
    label: FACET_LABELS[UploadLogFilterType.Company],
    type: BUILT_IN_FILTER_TYPES.COMPANY,
  },
  {
    key: UploadLogFilterType.UploadedBy,
    label: FACET_LABELS[UploadLogFilterType.UploadedBy],
    type: BUILT_IN_FILTER_TYPES.USER,
  },
  {
    key: UploadLogFilterType.Location,
    label: FACET_LABELS[UploadLogFilterType.Location],
    type: BUILT_IN_FILTER_TYPES.LOCATION,
  },
  {
    key: UploadLogFilterType.WorkflowCategory,
    label: FACET_LABELS[UploadLogFilterType.WorkflowCategory],
    type: BUILT_IN_FILTER_TYPES.WORKFLOW_CATEGORY,
  },
  {
    key: UploadLogFilterType.Formtype,
    label: FACET_LABELS[UploadLogFilterType.Formtype],
    type: BUILT_IN_FILTER_TYPES.WORKFLOW_TYPE,
  },
  {
    key: UploadLogFilterType.Filetype,
    label: FACET_LABELS[UploadLogFilterType.Filetype],
    type: BUILT_IN_FILTER_TYPES.FILE_TYPE,
  },
  {
    key: UploadLogFilterType.FileExtension,
    label: FACET_LABELS[UploadLogFilterType.FileExtension],
    type: BUILT_IN_FILTER_TYPES.FILE_EXTENSION,
  },
  {
    key: UploadLogFilterType.Source,
    label: FACET_LABELS[UploadLogFilterType.Source],
    type: BUILT_IN_FILTER_TYPES.WORKFLOW_STATUS,
  },
  {
    key: UploadLogFilterType.DateUploaded,
    label: FACET_LABELS[UploadLogFilterType.DateUploaded],
  },
]

interface IProps {
  store: AttachmentsStore
  state?: DesktopInitialState
  projectDateStore?: ProjectDateStore
  projectsStore?: ProjectsStore
  eventsStore?: DesktopEventStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  userProjectsStore?: UserProjectsStore
  tagsStore?: TagsStore
  workflows?: Map<string, Workflow>
  workflowCategoriesStore?: WorkflowCategoriesStore
  workflowTypesStore?: WorkflowTypesStore
  viewableFacets?: any
  totalFacets?: any
  isLoading?: boolean
  totalFacetsLoading?: boolean
  facetsLoading?: boolean
}

const AttachmentsHeaderBarComponent: React.FC<IProps> = ({
  store,
  state,
  projectDateStore,
  projectsStore,
  eventsStore,
  companiesStore,
  projectMembersStore,
  userProjectsStore,
  tagsStore,
  workflows,
  workflowCategoriesStore,
  workflowTypesStore,
  totalFacets,
  facetsLoading,
  isLoading,
}) => {
  const [isMenuShown, setIsMenuShown] = useState(false)
  const [searchKey, setSearchKey] = useState('')
  const [forceDatePickerClose] = useState(false)

  const toggleMenu = useCallback(() => {
    setIsMenuShown(prev => !prev)
  }, [])

  const changeSearchKey = useCallback((newSearchKey: string) => {
    setSearchKey(newSearchKey)
  }, [])

  const onShowChanged = useCallback(
    (isShown: boolean, filterType: string) => {
      if (!eventsStore) return
      const fieldsMap = eventsStore.appState.attachmentsFilters.fieldsMap

      if (isShown) {
        Object.keys(fieldsMap).forEach(otherFilterType => {
          if (otherFilterType !== filterType) {
            fieldsMap[otherFilterType].isFilterShown = false
          }
        })
      }

      const filterInfo = fieldsMap[filterType]
      if (filterInfo) {
        filterInfo.isFilterShown = isShown
      }
    },
    [eventsStore],
  )

  const onShowDatePickerChanged = useCallback(() => NOOP(), [])

  const headerBarStore = useMemo(() => {
    return new HeaderBarStore(state)
  }, [state])

  const attachmentsGroupingOptionsStore = useMemo(() => {
    return new AttachmentsGroupingOptionsStore(
      eventsStore,
      headerBarStore.onShowGroupByChanged,
      store.resetCollapsing,
      state.attachmentsFilters,
      attachmentsGroupingOptionList,
      AttachmentGroupingOption.Location,
      state.attachmentsFiltersSettings.desktopFilterMap,
      getAttachmentsGroupingOptionTranslatorKey,
    )
  }, [eventsStore, headerBarStore, store, state])

  const attachmentsSortingOptionsStore = useMemo(() => {
    return new AttachmentsSortingOptionsStore(
      store,
      headerBarStore.onShowSortByChanged,
    )
  }, [store, headerBarStore])

  const attachmentsFiltersStore = useMemo(() => {
    return new AttachmentFilterStore(
      eventsStore,
      store,
      onShowChanged,
      companiesStore,
      projectMembersStore,
      tagsStore,
      workflowCategoriesStore,
      workflowTypesStore,
      workflows,
    )
  }, [
    eventsStore,
    store,
    onShowChanged,
    companiesStore,
    projectMembersStore,
    tagsStore,
    workflowCategoriesStore,
    workflowTypesStore,
    workflows?.size || 0,
    facetsLoading,
    isLoading,
  ])

  useEffect(() => {
    if (!isLoading && totalFacets) {
      attachmentsFiltersStore.syncFilters()
    }
  }, [attachmentsFiltersStore, workflows, totalFacets, isLoading])

  const savedViewsAdapter = useAttachmentsSavedViews({
    projectId: state?.activeProject?.id,
    attachmentFilterStore: attachmentsFiltersStore,
    filters: state.attachmentsFilters,
    facets: ATTACHMENTS_FACETS,
  })

  const resetAllFilters = useCallback(() => {
    attachmentsFiltersStore.resetAllFilters()
    savedViewsAdapter.handleApplyFilters({}, '')
    if (store.isMyAttachmentsFilter) {
      store.toggleMyAttachmentsFilter()
    }
    if (store.isMyCompanyAttachmentsFilter) {
      store.toggleMyCompanyAttachmentsFilter()
    }
  }, [attachmentsFiltersStore, savedViewsAdapter, store])

  const handleMeFilterToggle = useCallback(() => {
    store.toggleMyAttachmentsFilter()
  }, [store])

  const handleMyCompanyFilterToggle = useCallback(() => {
    store.toggleMyCompanyAttachmentsFilter()
  }, [store])

  const changeDatePickerMode = useCallback(
    (mode: TwoMonthsDatePickerMode) => {
      store.changeDatePickerMode(mode)
    },
    [store],
  )

  const excludedFromSortKeys = useMemo(() => ['checkbox', 'name'], [])

  const renderDatePicker = useCallback(() => {
    const { dateFilters, selectedDatePickerMode } = store

    return (
      <DateSelector
        filters={dateFilters}
        forceClose={forceDatePickerClose}
        onShowChanged={onShowDatePickerChanged}
        mode={selectedDatePickerMode}
        projectsStore={projectsStore}
        projectDateStore={projectDateStore}
        onDateChange={store.changeDateFilter}
        shouldUseOtherDatesForFullProjectMode
        shouldShowTodayButton
        shouldRenderDateModes
        datePickerModes={datePickerStates}
        onOptionModeClick={changeDatePickerMode}
        shouldUseRelativePosition={false}
        todayStyle="px5"
        isAlternativeArrows
        isAlternativeButton
        shouldHideTopButtons
      />
    )
  }, [
    store,
    forceDatePickerClose,
    onShowDatePickerChanged,
    projectsStore,
    projectDateStore,
    changeDatePickerMode,
  ])

  const renderViewToggle = useCallback(() => {
    const { viewState, viewModes, setViewMode } = store
    return (
      <ViewModeToggle
        viewState={viewState}
        viewModes={viewModes}
        setViewMode={setViewMode}
      />
    )
  }, [store])

  const renderSearchFilter = useCallback(() => {
    return (
      <EntityNameFilter
        filters={state.attachmentsFilters}
        icon={
          <SearchIcon size={16} className="tw:shrink-0 tw:ml-1.5 tw:mr-1" />
        }
        shouldShowBaseText
        testId="attachments-search-input"
      />
    )
  }, [state])

  const renderMeAndMyCompanyFilters = useCallback(() => {
    const myCompanyId = UserProject.getCompanyId(state.user, userProjectsStore)
    const myCompany = companiesStore.getCompanyById(myCompanyId)

    return (
      <div className="row no-grow">
        <div
          onClick={handleMeFilterToggle}
          className={classList({
            'desktop-attachments-top-bar-btn avatar-filter mr8 gant-header-btn-outline': true,
            active: store.isMyAttachmentsFilter,
          })}
        >
          <Avatar
            src={state.user.avatarUrl}
            initials={state.user.initials}
            size={AvatarSize.Minuscule}
          />
          {Localization.translator.me}
        </div>
        <div
          onClick={handleMyCompanyFilterToggle}
          className={classList({
            'desktop-attachments-top-bar-btn avatar-filter gant-header-btn-outline': true,
            active: store.isMyCompanyAttachmentsFilter,
          })}
        >
          <Avatar
            src={myCompany?.avatarUrl}
            avatarType={AvatarType.Company}
            size={AvatarSize.Minuscule}
          />
          {Localization.translator.myCo}
        </div>
      </div>
    )
  }, [
    state,
    userProjectsStore,
    companiesStore,
    handleMeFilterToggle,
    handleMyCompanyFilterToggle,
    store.isMyAttachmentsFilter,
    store.isMyCompanyAttachmentsFilter,
  ])

  const renderGroupingPicker = useCallback(() => {
    return (
      <AttachmentsGroupingOptions
        store={attachmentsGroupingOptionsStore}
        forceClose={false}
        leftIcon={<ShapesIcon size={16} className="tw:shrink-0 tw:ml-1" />}
      />
    )
  }, [attachmentsGroupingOptionsStore])

  const renderSortingPicker = useCallback(() => {
    return (
      <AttachmentsSortingOptions
        store={attachmentsSortingOptionsStore}
        forceClose={headerBarStore.forceSortByClose}
      />
    )
  }, [attachmentsSortingOptionsStore, headerBarStore])

  const renderCollapseBtn = useCallback(
    (onClick: () => void, isExpand?: boolean) => {
      return (
        <Button variant="outline" size="icon" onClick={onClick}>
          {isExpand ? (
            <ChevronsUpDownIcon size={ICON_SIZE} />
          ) : (
            <ChevronsDownUpIcon size={ICON_SIZE} />
          )}
        </Button>
      )
    },
    [],
  )

  const renderColumnsConfiguration = useCallback(() => {
    if (!state.userActiveProjectSettings?.isAdmin) {
      return null
    }

    const {
      columns,
      updateColumnsConfiguration,
      resetColumnsConfiguration,
      isConfigurationLoading,
    } = store

    return (
      <div>
        <div className="row x-end">
          <div
            className={classList({
              'text grey large pointer no-grow icon-wrap row': true,
              'blue unclickable-element': isMenuShown,
            })}
            onClick={toggleMenu}
            title={Localization.translator.configureTableColumns}
          >
            <SettingsIcon
              size={16}
              className={classList({
                'tw:shrink-0 tw:mx-2.5 tw:rounded-full': true,
                'bg-blue-light': isMenuShown,
              })}
            />
          </div>
        </div>
        {isMenuShown && (
          <MenuCloser closeMenu={toggleMenu} className="row x-end mr40">
            <ColumnsConfigurationMenu
              columns={columns}
              restoreDefaults={resetColumnsConfiguration}
              searchKey={searchKey}
              changeSearchKey={changeSearchKey}
              updateColumnsConfiguration={updateColumnsConfiguration}
              excludedFromSort={excludedFromSortKeys}
              isLoading={isConfigurationLoading}
            />
          </MenuCloser>
        )}
      </div>
    )
  }, [
    state,
    store,
    isMenuShown,
    toggleMenu,
    searchKey,
    changeSearchKey,
    excludedFromSortKeys,
  ])

  const renderActionBar = useCallback(() => {
    const {
      displayedCount,
      isSomeRowSelected,
      selectedInstancesCount,
      collapseAllCategories,
      expandAllCategories,
      isAllCollapsed,
    } = store

    return (
      <div className="row py2">
        <div className="row no-grow mr10">
          {!isAllCollapsed && renderCollapseBtn(collapseAllCategories)}
          {isAllCollapsed && renderCollapseBtn(expandAllCategories, true)}
        </div>
        <div className="row text large light">
          <div className="pr10 no-grow nowrap">
            {isSomeRowSelected
              ? Localization.translator.xOfYSelected(
                  selectedInstancesCount,
                  displayedCount,
                )
              : Localization.translator.xFilesShowing(displayedCount)}
          </div>
        </div>
        <div className="row no-grow">{renderColumnsConfiguration()}</div>
      </div>
    )
  }, [store, renderCollapseBtn, renderColumnsConfiguration])

  return (
    <div className="header-bar col y-center x-between">
      <div className="col y-center flex-grow">
        <div className="row px16 bb-light-grey pt6 h55">
          <SavedViews
            views={savedViewsAdapter.savedViews}
            onViewSelect={savedViewsAdapter.handleApplyFilters}
            filterType="uploadLog"
            saveFilterMutation={savedViewsAdapter.saveFilterMutation}
            deleteFilterMutation={savedViewsAdapter.deleteFilterMutation}
            refetchQueryName="GetUploadLogCustomFiltersByProject"
            appliedFilters={savedViewsAdapter.appliedFilters}
            currentGrouping={savedViewsAdapter.currentGrouping}
            facets={savedViewsAdapter.facets}
            groupingOptions={savedViewsAdapter.groupingOptions}
          />
          <div className="row tw:grow-0!">
            {renderDatePicker()}
            {renderViewToggle()}
          </div>
        </div>

        <div className="row overflow-auto py8 px16 bb-light-grey">
          <div className="row attachments-0m-filter no-grow">
            {renderSearchFilter()}
          </div>
          <div className="separator mx10 no-grow" />
          <div className="row attachments-light-blue-filter no-grow attachments-0m-filter">
            {renderSortingPicker()}
          </div>
          <div className="separator mx10 no-grow" />
          <div className="row attachments-light-blue-filter no-grow attachments-0m-filter">
            {renderGroupingPicker()}
          </div>
          <div className="separator mx10 no-grow" />
          <div className="row no-grow">
            <div className="row attachments-0m-filter">
              {renderMeAndMyCompanyFilters()}
            </div>
            <AttachmentsFilters
              attachmentFilterStore={attachmentsFiltersStore}
              resetAllFilters={resetAllFilters}
            />
          </div>
        </div>
      </div>
      {renderActionBar()}
    </div>
  )
}

const AttachmentsHeaderBar = inject(
  'state',
  'projectDateStore',
  'projectsStore',
  'eventsStore',
  'companiesStore',
  'projectMembersStore',
  'userProjectsStore',
  'tagsStore',
  'workflowCategoriesStore',
  'workflowTypesStore',
)(observer(AttachmentsHeaderBarComponent))

export default AttachmentsHeaderBar

export const getAttachmentsGroupingOptionTranslatorKey = (
  id: string,
): KnownTranslatorKeys => {
  switch (id) {
    case AttachmentGroupingOption.None:
      return KnownTranslatorKeys.none
    case AttachmentGroupingOption.Date:
      return KnownTranslatorKeys.dateUploaded
    case AttachmentGroupingOption.Company:
      return KnownTranslatorKeys.company
    case AttachmentGroupingOption.UploadedBy:
      return KnownTranslatorKeys.uploadedBy
    case AttachmentGroupingOption.Location:
      return KnownTranslatorKeys.location
    case AttachmentGroupingOption.FileType:
      return KnownTranslatorKeys.fileType
    case AttachmentGroupingOption.FileCategory:
      return KnownTranslatorKeys.fileCategory
    case AttachmentGroupingOption.FormType:
      return KnownTranslatorKeys.workflowType
    case AttachmentGroupingOption.WorkflowCategory:
      return KnownTranslatorKeys.workflowCategory
    case AttachmentGroupingOption.WorkflowStatus:
      return KnownTranslatorKeys.workflowStatus
    default:
      throw new Error(id + ' is unhandled')
  }
}
